import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { mediaUrl } from "../mediaUrl";
import { FormatDownloadButtons } from "./FormatDownloadButtons";
import { EmailDesignModal } from "./EmailDesignModal";
import { ProUpsellCard } from "./ProUpsellCard";
import { useDownloadPrompts } from "../useDownloadPrompts";

interface StitchSimulatorProps {
  apiUrl: string;
  // Exactly one of these is set by the caller. assetId: a design generated
  // on /create, which has a GeneratedAsset row and so a text/font to show.
  // contentKey: a design uploaded through /convert or /replay, stored
  // content-addressed with no such row -- see GET
  // /api/pointcloud/content/{content_key} in app/routers/generate.py.
  assetId?: string;
  contentKey?: string;
  // Fired once the point cloud loads, so a page-level usePageMeta can set a
  // title like `Stitch Simulator: "Hello World"` -- this component owns no
  // page metadata itself (see the class doc below), but the data it fetches
  // is the only place that text/font/share_image comes from. share_image is
  // a root-relative media path, not an absolute URL -- the caller decides
  // how to make it absolute, since only the caller knows that's for a
  // share-preview tag specifically.
  onLoaded?: (data: { text?: string; font?: string; share_image?: string | null }) => void;
  // Behind share_buttons -- the point cloud response carries a files field
  // regardless, so this is what decides whether the download panel (see
  // .replay-layout in index.css) renders at all, not whether the data
  // exists.
  showDownloads?: boolean;
  // The token the email/Pro-upsell prompts below send to. Required for
  // those prompts, not for playback -- a caller with no token (there isn't
  // one today) simply never sees them.
  token?: string;
  emailCaptureEnabled?: boolean;
  proUpsellVariant?: string;
  // The asset id the email/Pro-upsell prompts use, when it differs from
  // `assetId` above: ReplayUploadPage plays back by contentKey (its upload
  // has no GeneratedAsset row of its own to be *played* by) but still gets
  // one back from /api/convert once a token is involved, purely so its
  // downloads can be emailed. Defaults to `assetId` -- ReplayPage's
  // generated-design case, where the same id serves both jobs.
  emailAssetId?: string;
  // Which page is actually rendering this -- same idea as emailAssetId's
  // split from assetId: this component is shared, but the two callers are
  // different flows (replaying an existing design vs. uploading a new
  // file), and email_capture_submitted needs to tell them apart.
  emailSource: "replay" | "replay_upload";
}

interface StitchPoint {
  x: number;
  y: number;
  stitch_type: "stitch" | "jump" | "trim" | "color_change" | "end" | "other";
  color: string; // hex, no leading '#'
}

interface PointCloud {
  points: StitchPoint[];
  total_stitches: number;
  // How many stitches downsampling took out of this design's files; 0 when
  // it never ran on them.
  stitches_removed?: number;
  width_inches: number;
  height_inches: number;
  // Only present for the assetId route -- an uploaded design has no design
  // text or font to report.
  text?: string;
  font?: string;
  // format -> media path, whatever the backend actually found on disk for
  // this content key (see _download_urls_for in generate.py). Always
  // present in the response; whether it's shown is showDownloads's job.
  files?: Record<string, string>;
  // This design's link-preview card (see build_share_card in
  // app/services/media.py) -- null if it hasn't been built for this design
  // yet. Deliberately not one of `files`: it isn't a downloadable format, and
  // reusing files.png here would reintroduce exactly the "unfurls as an
  // illegible sliver" bug the card exists to avoid.
  share_image?: string | null;
}

const SPEED_OPTIONS = [
  { label: "1x", stitchesPerSecond: 200 },
  { label: "3x", stitchesPerSecond: 600 },
  { label: "10x", stitchesPerSecond: 2000 },
];

// Non-sewing movement (needle up, machine repositioning or cutting thread).
// Drawn the same for both -- distinguishing them precisely matters to a
// digitizer, not to someone watching their design come together.
const NON_SEW_TYPES = new Set(["jump", "trim"]);

/**
 * The stitch-order canvas player: fetches a design's point cloud and
 * animates it sewing, stitch by stitch. Shared by two different pages --
 * ReplayPage (a dedicated /replay/{id} or /replay/content/{key} view, linked
 * to from a "Watch it stitch" result elsewhere) and ReplayUploadPage (the
 * /replay entry point itself, which shows this inline next to the upload
 * form rather than navigating to it) -- so it owns none of the page-level
 * concerns (usePageMeta, an <h1>) either of those already provides.
 */
export function StitchSimulator({
  apiUrl,
  assetId,
  contentKey,
  onLoaded,
  showDownloads,
  token,
  emailCaptureEnabled,
  proUpsellVariant: proUpsellFlag,
  emailAssetId,
  emailSource,
}: StitchSimulatorProps) {
  const [data, setData] = useState<PointCloud | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(SPEED_OPTIONS[0]!.stitchesPerSecond);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Scaled screen-space coordinates, computed once per design -- the API
  // gives points in the pattern's native 0.1mm units, which say nothing
  // about how many canvas pixels they should span.
  const scaledPointsRef = useRef<{ x: number; y: number }[]>([]);
  const lastDrawnIndexRef = useRef(0);
  const animationRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);

  const posthog = usePostHog();

  const {
    emailPrompt,
    emailPromptSettled,
    proUpsellVariant,
    notifyDownload,
    showEmailDesignButton,
    openEmailPrompt,
    closeEmailPrompt,
    closeProUpsell,
    markProUpsellInterested,
  } = useDownloadPrompts({
    apiUrl,
    token: token || "",
    assetId: emailAssetId ?? assetId,
    emailCaptureEnabled: emailCaptureEnabled ?? false,
    proUpsellFlag: proUpsellFlag ?? "control",
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const url = assetId
          ? `${apiUrl}/api/pointcloud/${assetId}`
          : `${apiUrl}/api/pointcloud/content/${contentKey}`;
        const response = await fetch(url);
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || "Could not load this design");
        }
        const pointCloud: PointCloud = await response.json();
        if (cancelled) return;
        setData(pointCloud);
        onLoaded?.({ text: pointCloud.text, font: pointCloud.font, share_image: pointCloud.share_image });
        posthog?.capture("stitch_replay_opened", {
          asset_id: assetId,
          content_key: contentKey,
          total_stitches: pointCloud.total_stitches,
        });
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Could not load this design");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // posthog is stable across the component's life; including it would
    // only ever re-run this for an identity change, not a real reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, assetId, contentKey]);

  // Fit the design's native units to the canvas once per load. No Y flip:
  // pyembroidery's stitch coordinates already increase downward, the same
  // as <canvas> -- confirmed against the app's own PNG writer
  // (PngWriter.write, which plots y directly with no flip either). Adding
  // one here, as an earlier version of this page did, renders every glyph
  // upside down.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!data || !canvas) return;

    const padding = 20;
    // Bounds come from real stitches only. Jumps and trims are the needle
    // moving with the thread up -- a single one of those (a "return to
    // start" or a long repositioning jump, both routine) can land far
    // outside the design itself, and including it here blows the bounding
    // box out enough that the actual design scales down to an invisible
    // speck in the corner.
    const sewPoints = data.points.filter((p) => p.stitch_type === "stitch");
    const boundsSource = sewPoints.length > 0 ? sewPoints : data.points;
    const xs = boundsSource.map((p) => p.x);
    const ys = boundsSource.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanX = Math.max(maxX - minX, 1);
    const spanY = Math.max(maxY - minY, 1);

    const availableWidth = canvas.width - padding * 2;
    const availableHeight = canvas.height - padding * 2;
    const scale = Math.min(availableWidth / spanX, availableHeight / spanY);
    const offsetX = (canvas.width - spanX * scale) / 2;
    const offsetY = (canvas.height - spanY * scale) / 2;

    scaledPointsRef.current = data.points.map((p) => ({
      x: offsetX + (p.x - minX) * scale,
      y: offsetY + (p.y - minY) * scale,
    }));

    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    lastDrawnIndexRef.current = 0;
    // Start from the first stitch, not the last: this is a replay of the
    // sewing order, and landing on the finished design reads as if nothing
    // is about to happen when Play is pressed.
    setCurrentIndex(0);
  }, [data]);

  const drawSegment = (ctx: CanvasRenderingContext2D, from: number, to: number) => {
    if (!data) return;
    const scaled = scaledPointsRef.current;
    for (let i = Math.max(from, 1); i <= to; i++) {
      const a = scaled[i - 1];
      const b = scaled[i];
      const point = data.points[i];
      if (!a || !b || !point) continue;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      if (NON_SEW_TYPES.has(point.stitch_type)) {
        ctx.strokeStyle = "rgba(150, 150, 150, 0.4)";
        ctx.lineWidth = 0.5;
      } else {
        ctx.strokeStyle = `#${point.color}`;
        ctx.lineWidth = 1.2;
      }
      ctx.stroke();
    }
  };

  // Incremental: only the newly-crossed segments are drawn each tick, so a
  // ~20,000-stitch design (real designs get that large) doesn't mean
  // redrawing the whole canvas 60 times a second.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    // currentIndex is fractional while playing -- each tick advances it by
    // elapsed-time * speed, which essentially never lands on a whole
    // number -- but scaledPointsRef is indexed by whole stitch number, and
    // `array[3.2]` is undefined in JS, not `array[3]`. Floor once here
    // rather than in drawSegment, so both callers (this effect, and the
    // scrub handler below, which already only ever sends whole numbers)
    // agree on what "drawn up to" means.
    const target = Math.floor(currentIndex);

    if (target < lastDrawnIndexRef.current) {
      // Scrubbed backward: canvas has no partial-erase, so redraw from zero.
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawSegment(ctx, 0, target);
    } else if (target > lastDrawnIndexRef.current) {
      drawSegment(ctx, lastDrawnIndexRef.current, target);
    }
    lastDrawnIndexRef.current = target;
    // drawSegment closes over `data`, which is what actually matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, data]);

  useEffect(() => {
    if (!playing || !data) return;

    const tick = (time: number) => {
      if (lastFrameTimeRef.current === null) lastFrameTimeRef.current = time;
      // Clamped, not the raw elapsed time: a backgrounded tab throttles or
      // fully pauses rAF (most browsers), so the callback after regaining
      // focus can report a `time` seconds or minutes past the last one.
      // Unclamped, that one frame would jump currentIndex (and the slider
      // reading it) most of the way to the end instead of resuming
      // smoothly -- this caps a single frame's advance at 100ms of
      // simulated playback regardless of how much real time actually
      // passed, so playback just looks briefly paused, never like it
      // teleported.
      const deltaSeconds = Math.min((time - lastFrameTimeRef.current) / 1000, 0.1);
      lastFrameTimeRef.current = time;

      setCurrentIndex((prev) => {
        const next = Math.min(prev + deltaSeconds * speed, data.total_stitches);
        if (next >= data.total_stitches) setPlaying(false);
        return next;
      });

      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      lastFrameTimeRef.current = null;
    };
  }, [playing, speed, data]);

  const togglePlay = () => {
    if (!data) return;
    // Restart from the top if it already finished, rather than a no-op Play.
    if (currentIndex >= data.total_stitches) setCurrentIndex(0);
    setPlaying((prev) => !prev);
  };

  const restart = () => {
    setPlaying(false);
    setCurrentIndex(0);
  };

  const handleDownload = (format: string, path: string) => {
    posthog?.capture("embroidery_file_downloaded", {
      format,
      font: data?.font,
      text_length: data?.text?.length,
      width_inches: data?.width_inches,
      height_inches: data?.height_inches,
      asset_id: assetId,
      from_replay: true,
      stitches_removed: data?.stitches_removed,
    });

    const downloadUrl = mediaUrl(apiUrl, path);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = path.split("/").pop() || `embroidery.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    notifyDownload(format);
  };

  const showDownloadPanel = showDownloads && data?.files && Object.keys(data.files).length > 0;
  const promptAssetId = emailAssetId ?? assetId;

  return (
    <>
      {loading && (
        <div className="empty-preview">
          <p aria-busy="true">Loading stitch data…</p>
        </div>
      )}

      {!loading && error && (
        <article className="error-message">
          <p><strong>Error:</strong> {error}</p>
        </article>
      )}

      {!loading && !error && data && (
        <div className={showDownloadPanel ? "replay-layout" : undefined}>
          <div className="replay-stage">
            <p className="replay-caption">
              {data.text ? `"${data.text}" in ${data.font}` : "Uploaded design"}
              {" · "}
              {data.width_inches.toFixed(2)}" × {data.height_inches.toFixed(2)}"
            </p>
            <canvas ref={canvasRef} width={800} height={600} className="replay-canvas" />
            <div className="replay-controls">
              <button type="button" onClick={togglePlay} style={{ minWidth: "5.5rem" }}>
                {playing ? "Pause" : currentIndex >= data.total_stitches ? "Replay" : "Play"}
              </button>
              <button type="button" className="secondary outline" onClick={restart}>
                Restart
              </button>
              <input
                type="range"
                min={0}
                max={data.total_stitches}
                value={Math.round(currentIndex)}
                onChange={(e) => {
                  setPlaying(false);
                  setCurrentIndex(Number(e.target.value));
                }}
              />
              {/* Its own full-width row under the slider (see .replay-count
                  in index.css), not beside the buttons: there isn't room
                  for both in .replay-stage, and here its ticking width
                  can't push anything around. */}
              <small className="replay-count">
                {Math.round(currentIndex).toLocaleString()} / {data.total_stitches.toLocaleString()} stitches
              </small>
              {/* Same toggle-group pattern as the text-alignment buttons on
                  CreatePage: a plain <select> read as a form field, not a
                  speed setting, and its options are invisible until opened.
                  A pressed/unpressed button group shows all three choices
                  and the current one at a glance. */}
              <div role="group" aria-label="Playback speed" className="replay-speed" style={{ display: "flex", width: "fit-content" }}>
                {SPEED_OPTIONS.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    className={speed === opt.stitchesPerSecond ? undefined : "outline secondary"}
                    aria-pressed={speed === opt.stitchesPerSecond}
                    onClick={() => setSpeed(opt.stitchesPerSecond)}
                    title={`${opt.label} speed`}
                    style={{ flex: "0 0 auto", width: "auto" }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {showDownloadPanel && (
            <div className="replay-downloads">
              <h3>Download this design</h3>
              <FormatDownloadButtons files={data.files!} onDownload={handleDownload} onEmailDesign={showEmailDesignButton ? openEmailPrompt : undefined} />
            </div>
          )}
        </div>
      )}

      {emailPrompt && promptAssetId && token && (
        <EmailDesignModal
          apiUrl={apiUrl}
          token={token}
          assetId={promptAssetId}
          format={emailPrompt.format}
          prefilledEmail={emailPrompt.prefilledEmail}
          requested={emailPrompt.requested}
          availableFormats={Object.keys(data?.files ?? {})}
          source={emailSource}
          onClose={closeEmailPrompt}
        />
      )}

      {/* Same sequencing as CreatePage/ConvertPage: waits for the
          design-email modal to close (or to never have opened at all)
          rather than rendering alongside it -- see ProUpsellCard's own
          comment. */}
      {proUpsellVariant && !emailPrompt && emailPromptSettled && (
        <ProUpsellCard variant={proUpsellVariant} onDone={closeProUpsell} onInterested={markProUpsellInterested} />
      )}
    </>
  );
}
